import type { Entity, Persisted } from '@tunasong/schemas'
import type { UseChildren } from './graph-types.js'
import { includeElement, isTaggedSysEntity } from '@tunasong/models'

/**
 * Render a `useEntityChild`/`useEntityChildren` filter into a string for telemetry.
 *
 * `JSON.stringify` returns `undefined` for function values, which produced the
 * useless "filter: undefined" Sentry messages in TUNASONG-VC. Prefer the
 * `EntityType` string when present, then the function's `name`, then a
 * placeholder so the resulting message is always grep-able.
 */
export const describeChildFilter = (filter: UseChildren['filter']): string => {
  if (typeof filter === 'string') {
    return filter
  }
  if (typeof filter === 'function') {
    return filter.name || '(anonymous filter)'
  }
  return '(no filter)'
}

/**
 * Render the duplicate children for telemetry. Only the id and type are
 * included so we never leak entity content into Sentry.
 */
export const summarizeDuplicateChildren = (children: Pick<Persisted<Entity>, 'id' | 'type'>[]): string =>
  children.map(({ id, type }) => `${type}#${id}`).join(', ')

export const getLatestEntry = <T extends Persisted<unknown>>({
  id,
  candidate,
  cache,
}: {
  id?: string | null
  cache: Map<string, T>
  // The candidate from the API - this may be outdated
  candidate?: T | null
}) => {
  if (!id) {
    return undefined
  }
  const cacheEntry = cache.get(id)
  if (!cacheEntry) {
    return candidate
  }
  if (!candidate) {
    return cacheEntry
  }

  // Always return the newest Entry
  if (cacheEntry.updatedAt > candidate.updatedAt) {
    return cacheEntry
  }
  return candidate
}

export const childFilter =
  ({ parentId, filter: type, includeSys }: UseChildren) =>
  (child: Entity) =>
    child &&
    child.parentId === parentId &&
    includeElement(child, type) &&
    !child.trash &&
    (includeSys ? true : !isTaggedSysEntity(child))

/**
 * Shape the result of an incremental child-entity page request. The lazy RTK Query
 * trigger's `unwrap()` can resolve to `undefined` when a request is aborted or its
 * cache entry is unsubscribed mid-flight (e.g., rapid parentId changes). Treat that
 * as an empty page rather than letting downstream code throw on `undefined.entities`.
 *
 * See Sentry TUNASONG-VB / TUNASONG-V6.
 */
export const shapeChildEntitiesPage = <T extends Entity>(
  data: { entities?: Persisted<Entity>[]; resumeKey?: string | null } | undefined,
  filterArgs: UseChildren
): { entities: T[]; resumeKey?: string | null } => ({
  ...data,
  entities: (data?.entities ?? []).filter(childFilter(filterArgs)) as unknown as T[],
})
